import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useStore } from '@/legacy/store/zustand';
import { COMPANY_SWITCH_MODAL } from '@/legacy/constants/modalTypes';
import { paths } from '@/app/router/path';
import { useDelegationsIds } from '../../hooks/useImpersonationScope';
import { getCompanyInitials } from '../domain/getCompanyInitials';
import { findCompanyColorIndex, getCompanyAvatarClass } from '../domain/companyAvatarColors';
import { resolveDelegationSubtitle } from '../domain/delegationSubtitle';
import type { DelegationSubtitle } from '../domain/delegationSubtitle';
import { useCompanySwitchScope } from './useCompanySwitchScope';

/** View model for the sidebar's company/delegation switch card. */
export const useCompanySwitch = () => {
	const navigate = useNavigate();
	const openModal = useStore((state) => state.openModal);
	const delegationsIds = useDelegationsIds();

	const {
		userInfo,
		hasDelegationAccessPermission,
		hasSettingsAccess,
		companies,
		isLoadingCompanies,
		delegationsOptions,
		capabilities,
	} = useCompanySwitchScope();

	const companyName = userInfo?.companyName ?? null;
	const isReady = Boolean(companyName);

	const colorIndex = findCompanyColorIndex(companies, userInfo?.companyId);
	const avatarClass = getCompanyAvatarClass(colorIndex);
	const initials = getCompanyInitials(companyName);

	const subtitle: DelegationSubtitle = resolveDelegationSubtitle({
		canSeeDelegations: hasDelegationAccessPermission,
		delegationsOptions,
		delegationsIds,
	});

	const onSwitchClick = useCallback(() => {
		if (capabilities.canOpenSwitcher) openModal(COMPANY_SWITCH_MODAL, {});
	}, [capabilities.canOpenSwitcher, openModal]);

	const onSettingsClick = useCallback(() => {
		navigate(paths.settings.main());
	}, [navigate]);

	return {
		isReady,
		companyName,
		initials,
		avatarClass,
		isLoadingCompanies,
		subtitle,
		isInteractive: capabilities.canOpenSwitcher,
		onSwitchClick,
		hasSettingsAccess,
		onSettingsClick,
	};
};
